import * as React from 'react';

import { cva } from 'class-variance-authority';
import { cn } from './utils';
import Spacer from '@/common/components/spacer';

const inputVariants = cva(
  'transition-colors px-4 py-3 outline-none body rounded-2xl flex items-center border-2 shadow-inputShadow focus:border-flexcar-blue40 focus:shadow-none disabled:cursor-not-allowed',
  {
    variants: {
      variant: {
        light:
          'border-flexcar-neutral80 placeholder-flexcar-neutral50 disabled:bg-neutral-90 disabled:text-flexcar-neutral50 disabled:placeholder-flexcar-neutral70',
        dark: 'border-flexcar-neutral20 text-flexcar-neutral100 placeholder-flexcar-neutral60 bg-flexcar-neutral10 disabled:text-flexcar-neutral60 disabled:placeholder-flexcar-neutral50',
      },
    },
  }
);

export type InputVariant = 'light' | 'dark';
export interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  variant?: InputVariant;
  errorText?: string | undefined;
  helperText?: string;
}

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  (
    {
      helperText,
      'aria-labelledby': ariaLabelledBy,
      errorText,
      className,
      variant = 'light',
      ...props
    }: InputProps,
    ref
  ) => {
    // Generate ids for accessibility
    const errorTextId = (props.id ? props.id + '-' : '') + 'error-text';
    const helperTextId = (props.id ? props.id + '-' : '') + 'helper-text';
    const ariaLabels = (() => {
      const labels = [];
      if (ariaLabelledBy !== undefined) {
        labels.push(ariaLabelledBy);
      }
      if (props.id !== undefined) {
        if (helperText !== undefined) {
          labels.push(helperTextId);
        }
        if (errorText !== undefined) {
          labels.push(errorTextId);
        }
      }
      return labels.join(' ');
    })();

    return (
      <>
        <input
          aria-labelledby={ariaLabels}
          className={cn(
            inputVariants({
              variant,
              className,
            }),
            {
              'border-flexcar-red40 shadow-none focus:border-flexcar-red40':
                errorText !== undefined,
            }
          )}
          ref={ref}
          {...props}
        />

        {helperText !== undefined && errorText === undefined && (
          <>
            <Spacer variant="height4" />
            <p
              id={helperTextId}
              className={cn({
                'body3 text-flexcar-neutral1': variant === 'light',
                'body3 text-flexcar-neutral100': variant === 'dark',
              })}
            >
              {helperText}
            </p>
          </>
        )}

        {errorText !== undefined && (
          <>
            <Spacer variant="height4" />
            <p id={errorTextId} className="body3 text-flexcar-red40">
              {errorText}
            </p>
          </>
        )}
      </>
    );
  }
);
Input.displayName = 'Input';

export { Input };
